import { yupResolver } from "@hookform/resolvers/yup";
import useIsMounted from "ismounted";
import { merge } from "lodash";
import { FunctionComponent, useContext, useEffect, useMemo } from "react";
import {
  Controller,
  DeepPartial,
  FormProvider,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { reach } from "yup";
import { Country } from "../../../../../../../../components/forms/country";
import { DateField } from "../../../../../../../../components/forms/date";
import { TextField } from "../../../../../../../../components/forms/text";
import { StepperContext } from "../../../../../../../../components/stepper/context";
import { VerificationDTO } from "../../../../../../../../store/verification/models/dto/due-diligence";
import { Container } from "../../../../common/components";
import { ConfigContext } from "../../../../common/config-context";
import { KycTypes } from "../../../../common/pages/kyc-details/kyc-type";
import { Phone } from "../../../../common/pages/kyc-details/phone";
import { SocialMediaLinks } from "../../../../common/pages/kyc-details/social-media-links";

type KycRequest = VerificationDTO.Requests.DueDiligence.Form.Post.Final.T["kycRequests"][number];

interface IUserInfoProps {
  setData: (d: DeepPartial<KycRequest>) => void;
  data: DeepPartial<KycRequest>;
}

const Wrapper2 = styled.div`
  width: 100%;
  max-width: 50em;
  margin: auto;
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 15px;
  align-items: center;

  & > *:nth-child(2n - 1) {
    text-align: right;
  }
`;

const UserInfo: FunctionComponent<IUserInfoProps> = ({ setData, data }) => {
  const { t } = useTranslation("fields");
  const isMounted = useIsMounted();
  const {
    state: { dueDiligenceId },
  } = useContext(ConfigContext);
  const { setValidation } = useContext(StepperContext);
  const {
    singleKycRequest,
  } = VerificationDTO.Requests.DueDiligence.Form.Post.Final.useValidations(
    dueDiligenceId
  );

  const newValidation = useMemo(
    () =>
      singleKycRequest
        .pick([
          "note",
          "mobileCountryCode",
          "mobileNumber",
          "socialMediaLinks",
          "dueDiligenceKycTypes",
        ])
        .shape({
          userDetails: reach(singleKycRequest, "userDetails").omit(["address"]),
        }),
    [singleKycRequest]
  );

  const form = useForm<
    Pick<
      KycRequest,
      | "note"
      | "mobileCountryCode"
      | "mobileNumber"
      | "socialMediaLinks"
      | "dueDiligenceKycTypes"
    > & {
      userDetails: Omit<KycRequest["userDetails"], "address">;
    }
  >({
    resolver: yupResolver(newValidation),
    defaultValues: useMemo(() => {
      const result = {};
      merge(
        result,
        {
          userDetails: {
            locale: "en",
          },
          socialMediaLinks: [],
        },
        data
      );
      return result as typeof data;
    }, [data]),
    delayError: 300,
  });

  const { handleSubmit, control } = form;

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        handleSubmit((d) => {
          setData(d);
          res();
        })();
      })
    );
    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [setData, setValidation, handleSubmit, isMounted.current]);

  return (
    <FormProvider {...form}>
      <Wrapper2>
        <Wrapper>
          <span>{t("first-name")}</span>
          <Controller
            control={control}
            name="userDetails.firstName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <span>{t("middle-name")}</span>
          <Controller
            control={control}
            name="userDetails.middleName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <span>{t("last-name")}</span>
          <Controller
            control={control}
            name="userDetails.lastName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <span>{t("email")}</span>
          <Controller
            control={control}
            name="userDetails.emailAddress"
            render={({ field, fieldState: { error } }) => (
              <TextField
                type="email"
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <span>{t("date-of-birth")}</span>
          <Controller
            control={control}
            name="userDetails.dateOfBirth"
            render={({ field, fieldState: { error } }) => (
              <DateField
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          <span>{t("nationality")}</span>
          <Controller
            control={control}
            name="userDetails.nationality"
            render={({ field, fieldState: { error } }) => (
              <Country
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <span>Phone</span>
          <Phone />
          <span>Individual Role</span>
          <Container>
            <KycTypes />
          </Container>
          <span>{t("note")}</span>
          <Controller
            control={control}
            name="note"
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <span>Social Media Links</span>
          <SocialMediaLinks />
        </Wrapper>
      </Wrapper2>
    </FormProvider>
  );
};

export default UserInfo;
